<template>
  <main class="overflow-hidden">
    <navigation />

    <section class="container font-gotham-rb">
      <span class="d-none" id="warehouse-rental-service-id">{{ warehouseRental.id }}</span>

      <title-section :title="currentTitle" :tag="titleTag" />

      <section class="py-4">
        <transition :name="directionAnimation" mode="out-in">

          <div class="row" v-if="currentStep === 'volume'" key="volume">
            <div class="col col-sm-10 col-md col-xxl-10 mx-auto">
              <volume />
            </div>
          </div>

          <div class="row d-flex" v-else-if="currentStep === 'packing-list'" key="packing-list">
            <div class="col col-md-7 col-lg-5 col-xxl-4 mx-auto">
              <packing-list
                :items-category-list="itemsCategoryList"
                :value="warehouseListItems"
              >
              </packing-list>
            </div>
          </div>

          <section v-else-if="currentStep === 'start-rental'" key="start-rental">
            <div class="row justify-content-center">
              <div class="col col-md-6 col-lg-4">
                <start-rental />
              </div>
            </div>

            <div class="row justify-content-center" v-if="warehouseRental.startRental || warehouseRental.indefiniteStartRental">
              <div class="col col-md-7 col-lg-5 px-lg-0 mt-4 start-rental-bot-message">
                <bot-message :textbot="startRentalAnswerMessage" />
              </div>
            </div>
          </section>

          <section v-else-if="currentStep === 'desired-location'" key="desired-location">
            <div class="row">
              <div class="col col-md-10 col-lg-9 col-xxl-8 mx-auto">
                <desired-location />
              </div>
            </div>

            <div class="row mt-4 text-center">
              <div class="col col-md-9 col-lg-6 col-xxl-5 mx-auto">
                <can-move-items />
              </div>
            </div>

            <div class="row justify-content-center mt-4" v-if="!addressConfig.stateZoneId && addressConfig.googleOptionSelected">
              <div class="col col-md-9 col-lg-6 col-xxl-5">
                <bot-message :textbot="desiredLocationAlternativeText" />
              </div>
            </div>
          </section>

          <section v-else-if="currentStep === 'loading'" key="loading">
            <loading-section />

            <div class="row justify-content-center d-flex mt-4 ">
              <div class="col col-md-7 col-lg-5 col-xxl-4 mx-auto">
                <bot-message textbot="Mientras esperamos tu precio… ¿Sabes qué es lo mejor de nuestras bodegas?" />
              </div>
            </div>
          </section>

          <section v-else-if="currentStep === 'client-info'" key="client-info">
            <client-info />

            <div class="row justify-content-center mt-4 mb-2 mb-md-0">
              <div class="col col-md-7 col-lg-6 col-xxl-4">
                <bot-message textbot="👉 No te preocupes, no compartiremos tu información con nadie 🤝 ✅" />
              </div>
            </div>
          </section>
        </transition>
      </section>

      <div class="row" v-if="errorMessage && (!enableNextStepButton || customErrorActive)">
        <div class="col col-md-10 col-lg-7 col-xl-6 mx-auto text-center">
          <div class="alert p-2 primary-border-color primary-text-color fs-7" role="alert" v-html="errorMessage">
          </div>
        </div>
      </div>

      <section class="my-1 my-md-3 row" v-if="!loadingStep && !loading">
        <div class="col col-md-6 col-lg-4 col-lg-3 col-xxl-3 px-md-3 px-xxl-0 mx-auto text-center">
          <button
            @click="nextStep"
            class="btn p-0 font-gotham-bd fs-5 text-white next-button"
            :class="[enableNextStepButton ? 'primary-bg-color' : 'btn-secondary border-0']"
          >
            {{ nextStepButtonName }}
          </button>

          <template v-if="canGoBack && currentStep !== 'client-info'">
            <hr class="w-75 mx-auto mt-3 mb-2">

            <button
              class="btn p-0 font-gotham-m text-secondary opacity-50 back-button"
              @click="backStep"
            >
              Volver atrás
            </button>
          </template>
        </div>
      </section>

      <div v-if="currentStep === 'client-info'" class="text-center">
        <div class="row mt-3 mb-2" v-if="loading">
          <svg class="fa-spin primary-svg-loading me-1" width="46" height="46" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="54" fill="none" stroke="#e6e6e6" stroke-width="8" />
            <circle cx="60" cy="60" r="54" fill="none" stroke="#ED3426" stroke-width="8" style="stroke-dasharray: 339.292;stroke-dashoffset: 130;" />
          </svg>
        </div>

        <div class="row mt-4 pt-2 my-md-2">
          <div class="col col-md-9 col-lg-6 col-xxl-5 mx-auto">
            <terms-and-conditions />
          </div>
        </div>
      </div>

      <div v-if="currentStep === 'client-info' && !loading" class="col col-md-6 col-lg-4 col-lg-3 col-xxl-3 mx-auto text-center mt-4">
        <hr class="w-75 mx-auto mt-4 mb-2">

        <button
          class="btn p-0 font-gotham-m text-secondary opacity-50 back-button"
          @click="backStep"
        >
          Volver atrás
        </button>
      </div>
    </section>

  </main>
</template>

<script>
import TitleSection from '../bot/components/Title.vue';
import Navigation from '../bot/components/Navigation.vue';
import BotMessage from '../bot/components/BotMessage.vue';
import TermsAndConditions from '../bot/components/TermsAndConditions.vue';
import CanMoveItems from '../bot/components/CanMoveItems.vue';
import PackingList from '../bot/components/PackingList.vue';

import ClientInfo from '../bot/components/steps/ClientInfo.vue';
import DesiredLocation from '../bot/components/steps/DesiredLocation.vue';
import StartRental from '../bot/components/steps/StartRental.vue';
import Volume from '../bot/components/steps/Volume.vue';
import LoadingSection from '../bot/components/steps/LoadingSection.vue';

import stepMixin from './mixins/steps';

export default {
  mixins: [stepMixin],

  components: {
    TitleSection,
    Navigation,
    ClientInfo,
    DesiredLocation,
    PackingList,
    StartRental,
    Volume,
    LoadingSection,
    BotMessage,
    TermsAndConditions,
    CanMoveItems,
    PackingList
  }
}
</script>

<style scoped>
.back-button { font-size: .75rem; }

.next-button {
  height: 38px;
  width: 100%;
}

@media (min-width: 768px) {
  .back-button { font-size: .875rem; }

  .next-button { width: 250px; }

  .start-rental-bot-message { max-width: 406px; }
}
</style>